import { FormEvent, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { admin, AdminUser } from "../../api/client";
import { useAuth } from "../../auth/AuthContext";
import { fmtDate, PageHeader, useFlash } from "./common";

export default function Users() {
  const { user: me } = useAuth();
  const [rows, setRows] = useState<AdminUser[]>([]);
  const [q, setQ] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const flash = useFlash(setToast);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ email: "", full_name: "", password: "", is_admin: false });

  const load = () => admin.users(q || undefined).then(setRows).catch((e) => setError(e.message));
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  async function update(u: AdminUser, body: Parameters<typeof admin.updateUser>[1]) {
    try {
      await admin.updateUser(u.id, body);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Update failed");
    }
  }

  async function resetPassword(u: AdminUser) {
    const password = prompt(`New password for ${u.email} (min 8 characters):`);
    if (!password) return;
    await update(u, { password });
    flash("Password updated.");
  }

  async function remove(u: AdminUser) {
    if (!confirm(`Delete ${u.email} and ALL their mailboxes, receipts and QuickBooks link? This cannot be undone.`)) return;
    try {
      await admin.deleteUser(u.id);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Delete failed");
    }
  }

  async function create(e: FormEvent) {
    e.preventDefault();
    try {
      await admin.createUser(form);
      setCreating(false);
      setForm({ email: "", full_name: "", password: "", is_admin: false });
      flash("User created.");
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not create user");
    }
  }

  return (
    <div className="page">
      <PageHeader title="Users">
        <button className="btn btn-primary" onClick={() => setCreating(true)}>+ Add user</button>
      </PageHeader>
      <div className="filter-bar">
        <input type="search" placeholder="Search email or name" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      {error && <div className="page-error">{error}</div>}
      <table className="data-table">
        <thead>
          <tr>
            <th>User</th>
            <th>Mailboxes</th>
            <th>Receipts</th>
            <th>Joined</th>
            <th>Last login</th>
            <th>Role</th>
            <th>Status</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.map((u) => (
            <tr key={u.id}>
              <td>
                <strong>{u.full_name || "—"}</strong>
                <div className="muted">{u.email}</div>
              </td>
              <td>{u.mailbox_count}</td>
              <td>
                <Link to={`/admin/messages?owner_user_id=${u.id}`}>{u.receipt_count}</Link>
              </td>
              <td>{fmtDate(u.created_at)}</td>
              <td>{fmtDate(u.last_login_at)}</td>
              <td>
                <label style={{ display: "flex", flexDirection: "row", gap: 6, margin: 0, fontWeight: 400 }}>
                  <input type="checkbox" checked={u.is_admin} disabled={u.id === me?.id}
                    onChange={(e) => update(u, { is_admin: e.target.checked })} style={{ width: "auto", margin: 0 }} />
                  Admin
                </label>
              </td>
              <td>
                <label style={{ display: "flex", flexDirection: "row", gap: 6, margin: 0, fontWeight: 400 }}>
                  <input type="checkbox" checked={u.is_active} disabled={u.id === me?.id}
                    onChange={(e) => update(u, { is_active: e.target.checked })} style={{ width: "auto", margin: 0 }} />
                  Active
                </label>
              </td>
              <td style={{ whiteSpace: "nowrap", textAlign: "right" }}>
                <button className="btn btn-ghost btn-sm" onClick={() => resetPassword(u)}>Reset password</button>
                {u.id !== me?.id && (
                  <button className="btn btn-danger btn-sm" onClick={() => remove(u)}>Delete</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {creating && (
        <div className="modal-backdrop" onClick={() => setCreating(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Add user</h2>
            <form onSubmit={create}>
              <label>Email<input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></label>
              <label>Full name<input type="text" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} /></label>
              <label>Temporary password<input type="text" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></label>
              <label style={{ display: "flex", flexDirection: "row", gap: 8, fontWeight: 400 }}>
                <input type="checkbox" checked={form.is_admin} onChange={(e) => setForm({ ...form, is_admin: e.target.checked })} style={{ width: "auto", margin: 0 }} />
                Administrator
              </label>
              <div className="modal-actions">
                <button type="button" className="btn btn-ghost" onClick={() => setCreating(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">Create</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}
